import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Images, Icons} from '../../assets';
import {ROUTES} from '../../constants/routes';
import {useAuth} from '../../contexts/auth.provider';
import {useOpportunity} from '../../contexts/opportunity.provider';
import {Opportunity, OpportunityStatus} from '../../models/opportunity';
import {OpportunitySearchParams, OpportunitySortBy} from '../../services/opportunity';
import {Button, ButtonSize, ButtonVariant} from '../../components/button';
import {DashboardLayout, EmptyState} from '../../components';
import {Heading, HeadingColor, HeadingLevel, HeadingVariant, HeadingWeight} from '../../components/typography/heading';
import {Stack, StackSpacing} from '../../components/layout';
import {Text, TextColor, TextVariant} from '../../components/typography/text';
import {OpportunityCard} from '../../components/opportunity-card';
import {OpportunityStatusFilter} from '../../components/opportunity-status-filter';

const Opportunities: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {getAllOpportunities} = useOpportunity();
  const {organizationInfo} = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialStatusParam = searchParams.get('status');
  const statusEnumValues = Object.values(OpportunityStatus);
  const initialSelectedStatus: OpportunityStatus =
    initialStatusParam && statusEnumValues.includes(initialStatusParam as OpportunityStatus)
      ? (initialStatusParam as OpportunityStatus)
      : OpportunityStatus.PUBLISHED;
  const [selectedStatus, setSelectedStatus] = useState<OpportunityStatus | null>(initialSelectedStatus);

  useEffect(() => {
    const statusParam = searchParams.get('status');
    const validStatuses = Object.values(OpportunityStatus);
    if (statusParam && validStatuses.includes(statusParam as OpportunityStatus)) {
      const next = statusParam as OpportunityStatus;
      if (selectedStatus !== next) setSelectedStatus(next);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        setLoading(true);
        const searchParams = new OpportunitySearchParams({
          organizationIds: organizationInfo?.organization?.id ? [organizationInfo.organization.id] : [],
          page: 1,
          limit: 10,
          sortBy: OpportunitySortBy.NEWEST,
          status: selectedStatus || undefined,
        });
        const response = await getAllOpportunities(searchParams);
        setOpportunities(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch opportunities');
      } finally {
        setLoading(false);
      }
    };

    if (organizationInfo?.organization?.id) {
      fetchOpportunities();
    }
  }, [getAllOpportunities, organizationInfo?.organization?.id, selectedStatus]);

  const handleSearchCandidates = () => {
    navigate(ROUTES.SEARCH_CANDIDATES);
  };

  const handleCreateOpportunity = () => {
    navigate(ROUTES.OPPORTUNITIES_CREATE);
  };

  const handleOpportunityUpdate = (updatedOpportunity: Opportunity) => {
    setOpportunities(prev => {
      if (selectedStatus && updatedOpportunity.status !== selectedStatus) {
        return prev.filter(opp => opp.id !== updatedOpportunity.id);
      }
      return prev.map(opp => (opp.id === updatedOpportunity.id ? updatedOpportunity : opp));
    });
  };

  const handleOpportunityDelete = (opportunityId: string) => {
    setOpportunities(prev => prev.filter(opp => opp.id !== opportunityId));
  };

  const handleStatusChange = (status: OpportunityStatus) => {
    setSelectedStatus(status);
    const params = new URLSearchParams(searchParams);
    params.set('status', status);
    setSearchParams(params, {replace: true});
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row gap-4 h-full p-4">
        <div className="flex-shrink-0 overflow-y-auto">
          <Stack spacing={StackSpacing.SM}>
            <button onClick={handleSearchCandidates} className="block w-full hover:opacity-80 transition-opacity cursor-pointer">
              <img src={Images.SEARCH_CANDIDATE_CARD} alt="Search candidates" className="w-full h-auto object-contain" />
            </button>

            <button onClick={handleCreateOpportunity} className="block w-full hover:opacity-80 transition-opacity cursor-pointer">
              <img src={Images.CREATE_OPPORTUNITY_CARD} alt="Create opportunity" className="w-full h-auto object-contain" />
            </button>
          </Stack>
        </div>

        <div className="lg:w-2/3 flex flex-col h-full">
          <div className="sticky pb-4 flex-shrink-0">
            <div className="flex justify-between items-center mb-6">
              <Heading level={HeadingLevel.H1} variant={HeadingVariant.PAGE_TITLE} weight={HeadingWeight.BOLD} color={HeadingColor.PRIMARY}>
                Opportunities
              </Heading>
              <Button
                variant={ButtonVariant.Primary}
                size={ButtonSize.SM}
                onClick={handleCreateOpportunity}
                startIcon={<img src={Icons.PLUS_SIGN} alt="Plus" className="w-5 h-5" />}>
                Create an Opportunity
              </Button>
            </div>

            <OpportunityStatusFilter selectedStatus={selectedStatus} onStatusChange={handleStatusChange} />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <Text variant={TextVariant.BODY} color={TextColor.MUTED}>
                  Loading opportunities...
                </Text>
              </div>
            )}

            {!loading && error && (
              <div className="flex items-center justify-center h-full">
                <Text variant={TextVariant.BODY} className="text-red-500">
                  Error: {error}
                </Text>
              </div>
            )}

            {!loading && !error && opportunities.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <EmptyState
                  icon={Icons.NO_OPPORTUNITY}
                  type={selectedStatus || OpportunityStatus.PUBLISHED}
                  onAction={handleCreateOpportunity}
                  actionIcon={<img src={Icons.PLUS_SIGN} alt="Plus" className="w-5 h-5" />}
                />
              </div>
            )}

            {!loading && !error && opportunities.length > 0 && (
              <div className="space-y-4">
                {opportunities.map((opportunity, i) => (
                  <OpportunityCard
                    key={opportunity.id || i}
                    opportunity={opportunity}
                    restrictedToCommunities={opportunity.restrictedToCommunities ?? []}
                    onOpportunityUpdate={handleOpportunityUpdate}
                    onOpportunityDelete={handleOpportunityDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Opportunities;
